<template>
  <Card height="full">
    <View gap="md" direction="column">
      <View align="center" gap="sm">
        <Icon :name="'file-check-02'" />
        <Text variant="h2">{{ capitalize(type) }} report</Text>
      </View>

      <View v-if="isLoading" justify="center" align="center" class="pdf-section">
        <LoadingIndicator />
      </View>

      <View v-else-if="error" justify="center" align="center" class="error-message" direction="column" gap="md">
        <IconTile icon="file-x-02" color="neutral" size="large" shape="rounded" :colored-icon="true" />

        <div>
          <Text variant="h3" align="center">Could not load the report</Text>
          <Text variant="body-large" color="tertiary">Please reload the page or try again later</Text>
        </div>
      </View>

      <Card class="pdf-section" variant="outline" v-else :style="{ opacity: iframeLoaded ? 1 : 0 }">
        <iframe
          v-if="report"
          ref="iframeRef"
          :srcdoc="report"
          class="full-width"
          frameborder="0"
          scrolling="no"
          @load="onIframeLoad"
        />
      </Card>
    </View>
  </Card>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, capitalize } from 'vue';

import { Card, LoadingIndicator, View, Icon, Text, IconTile } from '@tallo/design-system';

import { TransUnionProductType } from '../../enums/trans-union-product-type';

const props = defineProps<{
  report: string | undefined;
  type: TransUnionProductType;
  isLoading: boolean;
  error: string | undefined;
}>();

const iframeRef = ref<HTMLIFrameElement | null>(null);
const iframeLoaded = ref(false); // New ref to track iframe load state

function adjustIframeHeight() {
  if (iframeRef.value) {
    try {
      const iframe = iframeRef.value;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDocument) {
        const iframeHeight = iframeDocument.documentElement.scrollHeight || iframeDocument.body.scrollHeight;
        iframe.style.height = iframeHeight + 'px';
      }
    } catch (error) {
      console.error('Error adjusting iframe height:', error);
    }
  }
}

function injectCustomStyles() {
  if (iframeRef.value) {
    try {
      const iframe = iframeRef.value;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDocument) {
        const styleElement = iframeDocument.createElement('style');
        styleElement.type = 'text/css';
        styleElement.innerHTML = `
          .shareable\\.credit-site .shareable\\.credit-site-content {
            padding-top: 0 !important;
          }
        `;
        iframeDocument.head.appendChild(styleElement);
      }
    } catch (error) {
      console.error('Error injecting custom styles into iframe:', error);
    }
  }
}

function onIframeLoad() {
  adjustIframeHeight();
  injectCustomStyles();
  iframeLoaded.value = true;
}

watch(
  () => props.report,
  () => {
    iframeLoaded.value = false;
    adjustIframeHeight();
    injectCustomStyles();
  },
);

onMounted(() => {
  adjustIframeHeight();
  injectCustomStyles();
});
</script>

<style scoped>
.full-width {
  width: 100%;
  border: none;
}

.pdf-section,
.error-message {
  min-height: 65vh;
}
</style>
